import type { Audience } from "../license/claims";

// What a ping decides, and nothing else: no database, no HTTP, no clock of its own. Every input is
// an argument, so the rules below can be read and tested as rules.
//
// 🔴 The distinction this file exists for: telling "the hub said no" apart from "I did not reach the
// hub". The first must switch the installation off; the second must leave the cached token alone.
// Getting it backwards either cuts off a paying customer whose network blinked, or hands a permanent
// licence to anyone who unplugs the cable.
//
// And one deliberate divergence from the design we studied: an installation we do NOT KNOW is not
// refused — it is answered "unknown", which the installation reads as "not enrolled yet" and keeps
// whatever it holds. Only a positive no (revoked, cancelled) refuses. The reason is that the two
// mistakes do not cost the same: if our database ever comes back from an old backup, refusing would
// switch off customers who are paying, because of OUR error.

// How long an issued token lives. Short on purpose: renewal is what keeps a subscription alive, so a
// subscription that stops being renewed expires on its own and the hub never has to reach anything.
export const TOKEN_LIFETIME_SECONDS = 72 * 3600;

export interface License {
  id: string;
  product: Audience;
  plan: string;
  tenantLimit: number | null;
  status: "active" | "past_due" | "canceled";
}

export interface Installation {
  id: string;
  product: Audience;
  licenseId: string | null;
  revokedAt: Date | null;
}

// The single-use, short-lived code the install script carries. It is what replaces the customer
// copying a UUID from one screen into another.
export interface Enrollment {
  code: string;
  licenseId: string;
  expiresAt: Date;
  consumedAt: Date | null;
  // QUEM consumiu. É o que separa "este código já foi usado por outro" de "este código é meu, e eu
  // estou dizendo quem sou" — e sem essa distinção toda instalação para de sincronizar depois de
  // aderir (#32).
  consumedBy: string | null;
}

export interface PingRequest {
  installationId: string;
  product: Audience;
  // Reported so the panel can show what is running and offer support. Never used to decide.
  version: string | null;
  tenantCount: number | null;
  enrollmentCode: string | null;
}

export interface PingFacts {
  installation: Installation | null;
  // The licence bound to the installation, when there is one.
  boundLicense: License | null;
  // The enrolment named by the code, when a code was presented.
  enrollment: Enrollment | null;
  // The licence that enrolment points at.
  enrollmentLicense: License | null;
}

export type RefusalReason = "revoked" | "canceled" | "product_mismatch";

export type EnrollmentFailure =
  | "unknown_code"
  | "expired"
  | "already_used"
  | "license_inactive"
  | "wrong_product";

export type PingDecision =
  // Issue a token. `bindLicenseId` is set when this ping also attaches the installation to a licence.
  | {
      kind: "issue";
      license: License;
      bindLicenseId: string | null;
      consumeEnrollmentCode: string | null;
      createInstallation: boolean;
    }
  // Known, and legitimately without a licence. 200 and no token: the installation holds what the Free
  // edition holds, and nothing it already configured changes.
  | { kind: "unlicensed" }
  // Not known, and nothing presented to become known. 404 — the installation KEEPS its cache.
  | { kind: "unknown" }
  // A positive no. 403 — the installation stops using what the licence granted.
  | { kind: "refused"; reason: RefusalReason }
  // The code did not work. 409, with a reason a human can act on; nothing is switched off.
  | { kind: "enrollment_failed"; reason: EnrollmentFailure };

function enrollmentProblem(
  facts: PingFacts,
  req: PingRequest,
  now: Date,
): EnrollmentFailure | null {
  const { enrollment, enrollmentLicense } = facts;
  if (!enrollment) return "unknown_code";
  // Consumed is checked before expiry, because "this code was already used" is the more useful thing
  // to tell someone who ran the script twice, and an old code is usually both.
  if (enrollment.consumedAt) return "already_used";
  if (enrollment.expiresAt.getTime() <= now.getTime()) return "expired";
  if (!enrollmentLicense) return "unknown_code";
  if (enrollmentLicense.product !== req.product) return "wrong_product";
  if (enrollmentLicense.status === "canceled") return "license_inactive";
  return null;
}

export function decidePing(
  req: PingRequest,
  facts: PingFacts,
  now: Date = new Date(),
): PingDecision {
  const { installation, boundLicense } = facts;

  // A revoked installation is the one case that outranks everything, including a fresh code: whoever
  // revoked it meant it, and letting a new code undo that from the installation's side would make
  // revocation advisory.
  if (installation?.revokedAt) return { kind: "refused", reason: "revoked" };

  if (installation && installation.product !== req.product) {
    return { kind: "refused", reason: "product_mismatch" };
  }

  // A code is the re-licensing path as much as the first one, so it is read BEFORE the bound licence:
  // a customer whose licence was cancelled and who bought another must be able to come back.
  // 🔴 Adesão é IDEMPOTENTE. O script de instalação grava o código no ambiente e o cliente o manda em
  // TODA chamada, para sempre. Um código reapresentado por quem o consumiu não é erro: é a mesma
  // instalação dizendo quem é. Tratá-lo como falha fazia o ping terminar em 409 e a instalação ficar
  // sem renovar — perdendo a licença 72 h depois de instalar, pagando em dia (#32).
  const meuProprioCodigo =
    facts.enrollment?.consumedAt != null &&
    facts.enrollment.consumedBy === req.installationId;

  if (req.enrollmentCode !== null && !meuProprioCodigo) {
    const problem = enrollmentProblem(facts, req, now);
    if (problem) return { kind: "enrollment_failed", reason: problem };
    const license = facts.enrollmentLicense as License;
    return {
      kind: "issue",
      license,
      bindLicenseId: license.id,
      consumeEnrollmentCode: req.enrollmentCode,
      createInstallation: installation === null,
    };
  }

  if (!installation) return { kind: "unknown" };
  if (!boundLicense) return { kind: "unlicensed" };
  if (boundLicense.product !== req.product) {
    return { kind: "refused", reason: "product_mismatch" };
  }
  if (boundLicense.status === "canceled") {
    return { kind: "refused", reason: "canceled" };
  }
  // active and past_due both license. past_due is carried into the token so the operator can see it.
  return {
    kind: "issue",
    license: boundLicense,
    bindLicenseId: null,
    consumeEnrollmentCode: null,
    createInstallation: false,
  };
}
